import { useId, useRef, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MarketingPageHeader from "@/components/MarketingPageHeader";
import { Button } from "@/components/ui/button";
import { Globe, Users, BookOpen, MessageCircle, Sparkles, Video, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

// Served from public/ like the logo: an import would give the SSR bundle a
// different hashed URL than the client bundle, and a mismatched src breaks
// hydration of the server-rendered page.
const founderAntoine = "/images/about/founder-antoine.jpg";
const founderFred = "/images/about/founder-fred.jpg";
const founderJason = "/images/about/founder-jason.jpg";
const founderValentin = "/images/about/founder-valentin.jpg";
const mascotJason = "/images/about/mascot-jason.png";
const mascotValentin = "/images/about/mascot-valentin.png";

const FounderCard = ({ name, role, img, alt, mascot, bio }: { name: string; role: string; img: string; alt: string; mascot?: string; bio: string }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const bioId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  return (
    <div className="rounded-xl border border-border bg-card text-center overflow-hidden relative h-full">
      <div className="p-8 pb-4">
        {mascot ? (
          /* Avatar reveals the founder's mascot: hover on desktop, tap on touch.
             Tailwind gates group-hover behind @media(hover:hover), so the tap toggle
             is what makes the reveal reachable on mobile. Decorative (back is aria-hidden).
             role/tabIndex/keyboard make the toggle reachable without a pointer; the
             accessible name comes from the founder photo's alt text. */
          <div
            role="button"
            tabIndex={0}
            aria-pressed={flipped}
            className="group relative w-40 h-40 mx-auto mb-5 cursor-pointer [perspective:1000px] rounded-full focus-ring"
            onClick={() => setFlipped((f) => !f)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setFlipped((f) => !f);
              }
            }}
          >
            <div
              className={`relative w-full h-full transition-transform duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)] motion-reduce:transition-none ${
                flipped ? "[transform:rotateY(180deg)]" : ""
              }`}
            >
              {/* Front: founder photo */}
              <img
                src={img}
                alt={alt}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full rounded-full object-cover border-4 border-primary/15 shadow-soft [backface-visibility:hidden]"
              />
              {/* Back: founder's mascot on a fixed light disc so it stays legible in dark mode */}
              <div
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center rounded-full overflow-hidden border-4 border-primary/15 shadow-soft bg-zinc-100 [backface-visibility:hidden] [transform:rotateY(180deg)]"
              >
                <img src={mascot} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain p-3" />
              </div>
            </div>
          </div>
        ) : (
          /* No mascot yet: the photo alone, with no flip affordance promising one. */
          <img
            src={img}
            alt={alt}
            loading="lazy"
            decoding="async"
            className="w-40 h-40 mx-auto mb-5 rounded-full object-cover border-4 border-primary/15 shadow-soft"
          />
        )}
        <h3 className="font-display font-semibold text-foreground text-lg">{name}</h3>
        <p className="text-sm text-muted-foreground font-medium mb-3">{role}</p>
        {/* The open overlay covers this toggle, so it leaves the tab order while it is
            obscured and the overlay's own "Hide bio" is the reachable control. Closing
            still returns focus here programmatically, which works at tabIndex -1, and
            the toggle is tabbable again on the next render. */}
        <button
          ref={toggleRef}
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={bioId}
          tabIndex={open ? -1 : 0}
          className="inline-flex items-center gap-1 min-h-11 md:min-h-0 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          {open ? t("about.founders.hideBio") : t("about.founders.readMore")}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {/* Bio slides up over the card, capped short of the card's top so the rounded
          corners and the top of the photo stay visible above the text. Three cards
          share a row on desktop, so the cap is sized for the ~300-char locales at a
          304px card (Italian, Romanian) and overflow-y-auto keeps anything longer
          reachable instead of letting the card's overflow-hidden clip it. */}
      <div
        id={bioId}
        aria-hidden={!open}
        className={`absolute inset-x-0 bottom-0 max-h-[88%] overflow-y-auto bg-card/95 backdrop-blur-xs border-t border-border rounded-b-xl p-6 transition-all duration-300 ease-out motion-reduce:transition-none ${
          open ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
        }`}
      >
        <p className="text-sm text-muted-foreground leading-relaxed text-left">{bio}</p>
        {/* Closing from inside hands focus back to the always-visible toggle, so
            focus never sits on a control that is about to be hidden. */}
        <button
          onClick={() => {
            setOpen(false);
            toggleRef.current?.focus();
          }}
          tabIndex={open ? 0 : -1}
          className="mt-3 inline-flex items-center gap-1 min-h-11 md:min-h-0 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("about.founders.hideBio")}
          <ChevronDown className="w-3.5 h-3.5 rotate-180" />
        </button>
      </div>
    </div>
  );
};

const About = () => {
  const { t } = useTranslation();

  const values = [
    {
      icon: Users,
      titleKey: "about.values.humanConnection.title",
      descriptionKey: "about.values.humanConnection.description",
    },
    {
      icon: Sparkles,
      titleKey: "about.values.personalizedLearning.title",
      descriptionKey: "about.values.personalizedLearning.description",
    },
    {
      icon: Globe,
      titleKey: "about.values.globalCommunity.title",
      descriptionKey: "about.values.globalCommunity.description",
    },
  ];

  const howItWorks = [
    { step: "01", titleKey: "about.howItWorks.browseTutors.title", descriptionKey: "about.howItWorks.browseTutors.description", icon: BookOpen },
    { step: "02", titleKey: "about.howItWorks.bookSession.title", descriptionKey: "about.howItWorks.bookSession.description", icon: Video },
    { step: "03", titleKey: "about.howItWorks.learnGrow.title", descriptionKey: "about.howItWorks.learnGrow.description", icon: MessageCircle },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <MarketingPageHeader
        containerClassName="max-w-4xl"
        title={<>{t("about.hero.titleLine1")}<br className="hidden md:block" /> {t("about.hero.titleLine2")}</>}
        subtitle={t("about.hero.subtitle")}
      />

      {/* Values */}
      <section className="container mx-auto px-4 py-16 max-w-5xl">
        <div className="grid md:grid-cols-3 gap-6">
          {values.map((value) => (
            <div
              key={value.titleKey}
              className="rounded-xl bg-secondary/50 p-6"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <value.icon className="w-5 h-5 text-foreground" />
              </div>
              <h3 className="font-display font-semibold text-foreground mb-2">{t(value.titleKey)}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{t(value.descriptionKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-background">
        <div className="container mx-auto px-4 py-20 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-12 text-center">
            {t("about.howItWorks.heading")}
          </h2>
          <div className="space-y-8">
            {howItWorks.map((item) => (
              <div key={item.step} className="flex gap-6 items-start">
                <div className="shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-sm">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-display font-semibold text-foreground mb-1">{t(item.titleKey)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(item.descriptionKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="container mx-auto px-4 py-20 max-w-5xl">
        <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-12 text-center">
          {t("about.founders.heading")}
        </h2>
        {/* One column on phones, two from tablets up (2×2 with four founders). Three or
            four abreast would leave ~220-300px per card, where the bio overlay could not
            hold a bio without scrolling. Flex-wrap rather than a grid so an odd last card
            centres itself under the others. Cards on a line stretch to equal height,
            which the overlay's percentage cap relies on. */}
        <div className="flex flex-wrap justify-center gap-8">
          {[
            { name: "Valentin", role: t("about.founders.valentin.role"), img: founderValentin, alt: t("about.founders.valentin.alt"), mascot: mascotValentin, bio: t("about.founders.valentin.bio") },
            { name: "Jason", role: t("about.founders.jason.role"), img: founderJason, alt: t("about.founders.jason.alt"), mascot: mascotJason, bio: t("about.founders.jason.bio") },
            // Fred has no mascot yet — FounderCard renders the plain photo until one lands.
            { name: "Fred", role: t("about.founders.fred.role"), img: founderFred, alt: t("about.founders.fred.alt"), bio: t("about.founders.fred.bio") },
            // No mascot picture for Antoine either, so the plain photo until one exists.
            { name: "Antoine", role: t("about.founders.antoine.role"), img: founderAntoine, alt: t("about.founders.antoine.alt"), bio: t("about.founders.antoine.bio") },
          ].map((founder) => (
            <div key={founder.name} className="w-full md:w-[calc(50%_-_1rem)]">
              <FounderCard {...founder} />
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-background">
        <div className="container mx-auto px-4 py-20 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-12 text-center">
            {t("about.timeline.heading")}
          </h2>
          <div className="relative">
            <div className="absolute left-[19px] top-2 bottom-2 w-px bg-border" />
            <div className="space-y-10">
              {[
                { dateKey: "about.timeline.conversation.date", titleKey: "about.timeline.conversation.title", descriptionKey: "about.timeline.conversation.description" },
                { dateKey: "about.timeline.decision.date", titleKey: "about.timeline.decision.title", descriptionKey: "about.timeline.decision.description" },
                { dateKey: "about.timeline.building.date", titleKey: "about.timeline.building.title", descriptionKey: "about.timeline.building.description" },
                { dateKey: "about.timeline.recognition.date", titleKey: "about.timeline.recognition.title", descriptionKey: "about.timeline.recognition.description" },
                { dateKey: "about.timeline.beta.date", titleKey: "about.timeline.beta.title", descriptionKey: "about.timeline.beta.description" },
                { dateKey: "about.timeline.fredJoins.date", titleKey: "about.timeline.fredJoins.title", descriptionKey: "about.timeline.fredJoins.description" },
                { dateKey: "about.timeline.launch.date", titleKey: "about.timeline.launch.title", descriptionKey: "about.timeline.launch.description" },
                { dateKey: "about.timeline.antoineJoins.date", titleKey: "about.timeline.antoineJoins.title", descriptionKey: "about.timeline.antoineJoins.description" },
              ].map((item) => (
                <div key={item.dateKey} className="flex gap-6 items-start relative">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-card border-2 border-primary flex items-center justify-center z-10">
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">{t(item.dateKey)}</p>
                    <h3 className="font-display font-semibold text-foreground mb-1">{t(item.titleKey)}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(item.descriptionKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="container mx-auto px-4 py-20 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
          {t("about.different.heading")}
        </h2>
        <p className="text-muted-foreground leading-relaxed mb-6">
          {t("about.different.paragraph1")}
        </p>
        <p className="text-muted-foreground leading-relaxed">
          {t("about.different.paragraph2")}
        </p>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 pb-24 max-w-3xl text-center">
        <div className="rounded-2xl bg-primary/10 p-10">
          <h2 className="text-xl font-display font-bold text-foreground mb-2">{t("about.cta.heading")}</h2>
          <p className="text-muted-foreground mb-6 text-sm">
            {t("about.cta.subtitle")}
          </p>
          <Button asChild>
            <a href="mailto:hello@jynio.co">{t("about.cta.contactUs")}</a>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
